import React, { useState, useEffect } from "react";
import "./IframePage.css";
import hamburgerIcon from "../../assets/icons/hamburgerIcon.svg";
import closeIcon from "../../assets/icons/closeIcon.svg";
import addIcon from "../../assets/icons/addIcon.svg";
import BugIcon from "../../assets/icons/bugIcon.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setBugSummary,
  setIsBugSummaryModal,
} from "../../store/slice/bugSlice";
import { 
  getBugSummary, 
  getTasksByProjectRange, 
  convertToProxyUrl, 
  testProxyUrl, 
  checkProxyServer, 
  getBugsBySubtask,
  logUserTime 
} from "../../services/homeService";
import { getCookie } from "../../services/utils";
import BugSummary from "../BugList/BugSummary";
import BugTable from "../BugList/BugTable";
import loginData from "../BugList/userLoginIds.json";

const IframePage = () => {
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [showBugsList, setShowBugsList] = useState(false);
  const [rowNumber, setRowNumber] = useState("1");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noTasksFound, setNoTasksFound] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true); // State to track iframe loading state
  const [loadingError, setLoadingError] = useState(null); // State to track loading errors
  const [loadingStatus, setLoadingStatus] = useState("Loading..."); // State to track loading status message
  // Time tracking state
  const [taskStartTime, setTaskStartTime] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [isUserApproved, setIsUserApproved] = useState(false);
  
  // Create a ref for the iframe
  const iframeRef = React.useRef(null);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { clientId, projectId, subtaskId } = useParams();
  
  // Read parameters from URL search params since we're opening in a new tab
  const searchParams = new URLSearchParams(window.location.search);
  const clientName = searchParams.get('clientName') || (location.state?.clientName || '');
  const projectName = searchParams.get('projectName') || (location.state?.projectName || '');
  const guid = searchParams.get('guid') || (location.state?.guid || '');
  const subtaskName = searchParams.get('subtaskName') || (location.state?.subtaskName || '');
  const subtaskSummary = searchParams.get('subtaskSummary') || (location.state?.subtaskSummary || '');
  
  // Log the source of parameters
  console.log("Parameters source:", searchParams.toString() ? "URL query params" : "location state");
  console.log("Subtask Summary:", subtaskSummary);

  const { isBugSummaryModal, selectedProjectBugs } = useSelector((state) => state.bugDetails);

  // Function to parse range from summary (e.g., "Row 1-100" -> {start: 1, end: 100})
  const parseRangeFromSummary = (summary) => {
    if (!summary) return null;
    
    // Look for patterns like "Row 1-100", "1-50", etc.
    const rangeMatch = summary.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
      console.log("Parsed range from summary:", rangeMatch[1], rangeMatch[2]);

      return {
        start: parseInt(rangeMatch[1]),
        end: parseInt(rangeMatch[2])
      };
    }
    return null;
  };

  // Function to fetch tasks data from API
  const fetchTasksData = async () => {
    console.log("fetchTasksData called with subtaskSummary:", subtaskSummary);
    
    // Update loading status
    setLoadingStatus("Fetching questions data...");
    setLoading(true);
    
    if (!subtaskSummary) {
      console.warn("No subtask summary available");
      setLoadingError("No subtask summary available. Cannot determine question range.");
      setLoading(false);
      return;
    }

    const range = parseRangeFromSummary(subtaskSummary);
    if (!range) {
      console.warn("Could not parse range from summary:", subtaskSummary);
      setLoadingError(`Could not parse row range from subtask summary: "${subtaskSummary}". Expected format like "Row 1-100"`);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Extract projectId from URL params or use a default value
      const urlParams = new URLSearchParams(window.location.search);
      let projectId;
      
      // First try URL query params (for when opened in new tab)
      if (urlParams.get('projectId')) {
        projectId = urlParams.get('projectId');
        console.log("Using projectId from URL query params:", projectId);
      } 
      // Then try URL path (for when using React Router navigation)
      else if (location.pathname.split('/').length > 4) {
        projectId = location.pathname.split('/')[4];
        console.log("Using projectId from URL path:", projectId);
      }
      // Finally fall back to the projectId from useParams
      else {
        console.log("Using projectId from useParams:", projectId);
      }
      
      console.log("Fetching tasks with projectId:", projectId, "range:", range.start, "-", range.end);
      setLoadingStatus(`Fetching questions for rows ${range.start}-${range.end}...`);
      
      const response = await getTasksByProjectRange(projectId, range.start, range.end);
      console.log("API response:", response);
      
      // Extract tasks array from response object
      const tasks = response.tasks || [];
      
      // Check if no tasks were returned
      if (tasks.length === 0) {
        console.log('No tasks found for this subtask range');
        setLoadingStatus(`No questions found for rows ${range.start}-${range.end}`);
        setTasksData([]);
        setNoTasksFound(true);
        setLoading(false);
        return;
      }
      
      // Reset no tasks found state if we have data
      setNoTasksFound(false);
      
      // Transform API response to match the expected format
      const transformedData = tasks.map((task, index) => {
        try {
          return {
            id: task.id,
            url: task.src,
            localStorageKey: task.storageKey,
            localStorageValue: JSON.parse(task.storageValue || "{}"),
            rowNumber: task.rowNumber || (range.start + index), // Use range.start as base for row numbers
            slideNumber: task.slideNumber,
            loName: task.loName,
            loId: task.loId,
            assessmentTitle: task.assessmentTitle
          };
        } catch (parseError) {
          console.error(`Error parsing task ${index} localStorage value:`, parseError);
          return {
            id: task.id,
            url: task.src,
            localStorageKey: task.storageKey,
            localStorageValue: {},
            rowNumber: task.rowNumber || (range.start + index), // Use range.start as base for row numbers
            slideNumber: task.slideNumber,
            loName: task.loName,
            loId: task.loId,
            assessmentTitle: task.assessmentTitle
          };
        }
      });
      
      console.log('Raw tasks from API:', tasks);
      console.log('Transformed tasks data:', transformedData);
      console.log('Number of tasks:', transformedData.length);
      
      const firstRowNumber = transformedData[0]?.rowNumber || range.start;
      setLoadingStatus(`Found ${transformedData.length} questions for rows ${range.start}-${range.end}. Loading question ${firstRowNumber}...`);
      
      // Log each task's URL to verify they're different
      transformedData.forEach((task, index) => {
        console.log(`Task ${index + 1} URL:`, task.url);
        console.log(`Task ${index + 1} localStorage:`, task.localStorageKey, task.localStorageValue);
      });
      
      setTasksData(transformedData);
      console.log('Fetched tasks data:', transformedData);
    } catch (error) {
      console.error("Error fetching tasks data:", error);
      setLoadingError(`Error fetching questions: ${error.message}. Check browser console for details.`);
      setTasksData([]);
      setNoTasksFound(false); // Don't show "no tasks" message on API error
    } finally {
      setLoading(false);
    }
  };

  

  // Helper function to check if a URL is a Bento URL
  const isBentoUrl = (url) => {
    return url && url.includes("bento.mheducation.com");
  };

  // Get current data based on currentIndex - use fetched data if available, otherwise fall back to static
  const dataArray = tasksData.length > 0 ? tasksData : [];
  const currentData = dataArray[currentIndex] || dataArray[0];
  console.log("currentData: ",currentData)
  const staticUrl = currentData?.url;
  const staticLocalStorageKey = currentData?.localStorageKey;
  const staticLocalStorageValue = currentData?.localStorageValue;
  
  // Extract projectId for bug creation
  const urlParams = new URLSearchParams(window.location.search);
  const projectIdParam = urlParams.get('projectId') || projectId;

  const toggleLeftPanel = () => {
    setIsLeftPanelOpen(!isLeftPanelOpen);
  };

  const loadUrl = async (index = currentIndex) => {
    // Set iframe loading to true whenever we start loading a new URL
    setIframeLoading(true);
    
    const iframe = iframeRef.current;
    
    console.log('loadUrl called for index:', index);
    
    if (!iframe) {
      console.error("Iframe element not found in DOM");
      setLoadingError("Iframe element not found");
      setIframeLoading(false);
      return;
    }
    
    // Get data for the specific index
    const dataArray = tasksData.length > 0 ? tasksData : [];
    
    if (dataArray.length === 0) {
      console.error("No tasks data available");
      setLoadingError("Error: No tasks data available");
      setIframeLoading(false);
      return;
    }
    
    if (index < 0 || index >= dataArray.length) {
      console.error("Index out of range:", index, "Max index:", dataArray.length - 1);
      setLoadingError(`Error: Index ${index} out of range (max: ${dataArray.length - 1})`);
      setIframeLoading(false);
      return;
    }
    
    const targetData = dataArray[index];
    const url = targetData?.url;

    console.log('loadUrl - Index:', index);
    console.log('loadUrl - Tasks Data Length:', tasksData.length);
    console.log('loadUrl - Target Data:', targetData);
    console.log('loadUrl - URL:', url);

    if (!url) {
      console.error("No URL found for index:", index);
      setLoadingError(`Error: No URL available for task at index ${index}`);
      setIframeLoading(false);
      return;
    }

    // Update loading status
    setLoadingStatus("Loading via proxy...");
    setLoadingError(null);
    
    try {
      // Use the convertToProxyUrl function from homeService to get the proper URL
      const proxyUrl = convertToProxyUrl(url);
      
      if (!proxyUrl || proxyUrl === url) {
        console.error("Failed to convert to proxy URL:", url);
        setLoadingError(`Failed to convert URL to proxy format: ${url}`);
        setIframeLoading(false);
        return;
      }
      
      console.log("Loading for index:", index);
      console.log("Original URL:", url);
      console.log("Proxy URL:", proxyUrl);

      // Update loading status - use rowNumber for consistent display with input field
      const range = parseRangeFromSummary(subtaskSummary);
      const rangeStart = range ? range.start : 1;
      const actualRowNumber = targetData?.rowNumber || (rangeStart + index);
      
      setLoadingStatus(`Loading question ${actualRowNumber} of ${dataArray.length}...`);
      
      // Load the iframe with the proxied URL
      loadIframe(iframe, proxyUrl, targetData, index + 1, dataArray.length, dataArray);
    } catch (error) {
      console.error("Error in loadUrl:", error);
      setLoadingError(`Error loading URL: ${error.message}`);
      // Reset loading state on error to show the error message
      setIframeLoading(false);
    }
  };

  const loadIframe = (iframe, url, targetData = currentData, questionNumber, totalQuestions, dataArray) => {
    let loadTimeout;

    // Set iframe loading state to true to show the loading bar
    setIframeLoading(true);
    setLoadingError(null);

    console.log("🔄 Starting iframe reload for new task");
    console.log("🎯 Target URL:", url);
    console.log("📦 Target localStorage:", targetData?.localStorageKey, targetData?.localStorageValue);
    console.log("🔢 Question Number/Total:", questionNumber, "/", totalQuestions);

    // Verify iframe element
    if (!iframe) {
      console.error("❌ No iframe element provided to loadIframe");
      setLoadingError("Error: Iframe element not found");
      setIframeLoading(false);
      return;
    }

    // Force clear the iframe first to avoid cached content
    try {
      iframe.src = "about:blank";
      
      // Wait a moment then load the new URL
      setTimeout(() => {
        // Set a timeout for loading
        loadTimeout = setTimeout(() => {
          setLoadingError("Loading timeout! The page might be taking too long to load.");
          console.warn("iframe loading timeout");
        }, 20000); // 20 second timeout - increased from 15s

        // Load event handler
        iframe.onload = function () {
          clearTimeout(loadTimeout);
          console.log("✅ iframe loaded successfully for new task");

          // Check if iframe has content
        
            try {
              // Send new localStorage data for this specific task
              sendLocalStorageMessage(targetData);
              
              // Send localStorage for next question if available
              const currentIndex = questionNumber - 1;
              const nextIndex = currentIndex + 1;
              if (nextIndex < dataArray.length) {
                const nextData = dataArray[nextIndex];
                sendLocalStorageMessage(nextData);
              }
              
              try {
                const iframeDoc =
                  iframe.contentDocument || iframe.contentWindow.document;
                if (
                  iframeDoc &&
                  iframeDoc.body &&
                  iframeDoc.body.innerHTML.trim()
                ) {
                  // Set iframe loading state to false to hide loading and show iframe
                  setIframeLoading(false);
                  console.log("✅ iframe has content and localStorage updated");
                } else {
                  setLoadingError("iframe loaded but appears empty. Check browser console for details.");
                  // Even if iframe appears empty, we hide the loading message
                  setIframeLoading(false);
                }
              } catch (accessError) {
                // Cross-origin, can't check content - just show the iframe
                console.log("Cross-origin iframe - can't check content:", accessError);
                // Set iframe loading state to false to hide loading and show iframe
                setIframeLoading(false);
                console.log("✅ iframe loaded (cross-origin, localStorage sent)");
              }
            } catch (e) {
              console.error("Error in iframe onload handler:", e);
              setLoadingError(`Error processing iframe content: ${e.message}`);
              // Still show the iframe by setting loading state to false
              setIframeLoading(false);
            }
         
        };

        // Error event handler
        iframe.onerror = function (e) {
          clearTimeout(loadTimeout);
          console.error("iframe loading error:", e);
          setLoadingError("Error loading iframe. Check browser console for details.");
          // Reset loading state on error
          setIframeLoading(false);
        };

        // Log that we're about to set the src
        console.log("🔗 Loading new URL in iframe:", url);
        
        // Set the source URL
        iframe.src = url;
        
        // Confirm the src was set
        console.log("🔗 Final iframe URL:", iframe.src);
        
      }, 200); // Small delay to ensure iframe is cleared first
    } catch (error) {
      console.error("❌ Error in loadIframe:", error);
      setLoadingError(`Error loading iframe: ${error.message}`);
      // Reset loading state on error to show the error message
      setIframeLoading(false);
    }
  };

  const sendLocalStorageMessage = (targetData = currentData) => {
    
    const iframe = iframeRef.current;
    const key = targetData?.localStorageKey;
    const value = targetData?.localStorageValue;

    if (!key) {
      console.warn("⚠️ No localStorage key provided for this task");
      return;
    }

    if (!iframe || !iframe.contentWindow) {
      console.error("❌ iframe not ready for localStorage message");
      return;
    }

    console.log("📨 Sending NEW localStorage data to iframe:");
    console.log("🔑 Key:", key);
    console.log("📦 Value:", value);
    console.log("🎯 Target data:", targetData);
    
    // Wait a bit to ensure iframe is fully loaded before sending message

      try {
        // Send the localStorage message with multiple formats to ensure compatibility
        iframe.contentWindow.postMessage(
          {
            type: "setLocalStorage",
            command: "setLocalStorage",
            key: key,
            value: value,
          },
          "*" // Allow any origin for the postMessage
        );
        
        // Try an alternative message format as backup
        iframe.contentWindow.postMessage(
          {
            action: "setLocalStorage",
            key: key,
            value: value,
          },
          "*"
        );
        
        console.log("✅ localStorage message sent successfully");
      } catch (error) {
        console.error("❌ Error sending localStorage message:", error);
      }
 
  };

  // Check if proxy server is running using service
  const checkProxyServerStatus = async () => {
    try {
      // Show initial loading message
      setLoadingStatus("Checking proxy server availability...");
      
      // Check proxy URL configuration
      const bugReportingProxyURL = process.env.REACT_APP_BUG_REPORTING_PROXY_URL;
      console.log("Using proxy URL:", bugReportingProxyURL);
      
      if (!bugReportingProxyURL) {
        console.warn("⚠️ No proxy URL configured in .env file (REACT_APP_BUG_REPORTING_PROXY_URL)");
        setLoadingError("Proxy URL not configured. Please set REACT_APP_BUG_REPORTING_PROXY_URL in .env file.");
        return;
      }
      
      // Check proxy server status
      const result = await checkProxyServer();
      console.log("Proxy server check result:", result);
      
      // setLoadingStatus(result.message);
      
      // If proxy server is running, show success and proceed with loading data
      if (result.isRunning) {
        console.log("✅ Proxy server is running, proceeding with data loading");
        // Fetch tasks data from API - add delay to ensure message is visible
        setTimeout(() => {
          console.log("Starting to fetch tasks data...");
          fetchTasksData();
        }, 1000);
      } else {
        console.warn("⚠️ Proxy server is not available");
      }
    } catch (error) {
      console.error("Error checking proxy server:", error);
      setLoadingError("Error checking proxy server. Please try refreshing the page.");
    }
  };

  // Function to log time spent on a task
  const logTimeSpent = async (taskId, durationSeconds) => {
    // Return resolved promise for early returns to maintain consistency
    if (isUserApproved) {
      console.log("Skipping time logging: user is approved");
      return Promise.resolve();
    }
    
    if (!taskId) {
      console.log("Skipping time logging: no task ID provided");
      return Promise.resolve();
    }
    
    if (durationSeconds <= 0) {
      console.log("Skipping time logging: invalid duration");
      return Promise.resolve();
    }

    // Get user ID from cookies
    const email = getCookie('email');
    const userId = getCookie('userGuid');
    
    if (!userId || !email) {
      console.warn("Cannot log time: User information not available");
      return Promise.resolve();
    }

    console.log(`Logging time: ${durationSeconds} seconds for task ID: ${taskId}`);
    
    const timeEntryData = {
      taskId: parseInt(taskId),
      userEmail: email,
      durationSeconds: durationSeconds,
      description: `Time spent reviewing task ${taskId}`,
      entryDate: new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format
    };
    
    try {
      const response = await logUserTime(timeEntryData);
      console.log("Time entry logged successfully:", response);
      return Promise.resolve(response); // Return the response for potential chaining
    } catch (error) {
      console.error("Error logging time:", error);
      // Re-throw the error to be caught by the caller
      return Promise.reject(error);
    }
  };

  // Function to start tracking time for a task
  const startTaskTimer = (taskId) => {
    if (isUserApproved) return;
    
    setTaskStartTime(Date.now());
    setCurrentTaskId(taskId);
    console.log(`Started timer for task ID: ${taskId}`);
  };

  // Function to end tracking time and log it
  const endTaskTimer = async () => {
    // Return early with a resolved promise if conditions aren't met
    if (isUserApproved || !taskStartTime || !currentTaskId) {
      return Promise.resolve();
    }
    
    const endTime = Date.now();
    const durationSeconds = Math.round((endTime - taskStartTime) / 1000);
    
    // Capture current task ID and start time before resetting
    const taskId = currentTaskId;
    const startTime = taskStartTime;
    
    // Reset state immediately to avoid race conditions with new task timers
    setTaskStartTime(null);
    setCurrentTaskId(null);
    
    // Only log time if more than 1 second was spent on the task
    if (durationSeconds > 0) {
      try {
        // Return the promise from logTimeSpent
        return logTimeSpent(taskId, durationSeconds)
          .then(() => {
            console.log(`Successfully logged ${durationSeconds} seconds for task ID: ${taskId}`);
          })
          .catch((error) => {
            console.error(`Failed to log time for task ID: ${taskId}`, error);
          });
      } catch (error) {
        console.error(`Error in endTaskTimer for task ID: ${taskId}`, error);
        return Promise.resolve(); // Still resolve the promise to not block UI
      }
    } else {
      console.log(`Skipping time log for task ID: ${taskId} due to minimal time spent (${durationSeconds} seconds)`);
      return Promise.resolve();
    }
  };

  // Check if user is approved
  const checkUserApproval = () => {
    const email = getCookie('email');
    const role = getCookie('role');
    
    // Check if user is admin or has an approved role
    const isApproved = loginData.login.includes(email);
    
    setIsUserApproved(isApproved);
    console.log("User approval status:", isApproved);
  };

  useEffect(() => {
    // Set iframe loading state to true when component mounts
    setIframeLoading(true);
    
    // Check user approval status
    checkUserApproval();
    
    // Start by checking the proxy server status
    checkProxyServerStatus();
    
    // Listen for responses from iframe postMessage
    const handleMessage = (event) => {
      console.log("Received response from iframe:", event.data);
      if (event.data && event.data.status) {
        if (event.data.status === "success") {
          console.log("✅ localStorage set successfully for key:", event.data.key);
        } else if (event.data.status === "error") {
          console.error("❌ Error setting localStorage:", event.data.error);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    
    // Add beforeunload event listener to catch page closes/refreshes
    const handleBeforeUnload = async (e) => {
      // Log time when the page is being closed or refreshed
      if (taskStartTime && currentTaskId) {
        console.log(`Logging time for task ID: ${currentTaskId} before unloading page`);
        const endTime = Date.now();
        const durationSeconds = Math.round((endTime - taskStartTime) / 1000);
        
        // Try to log immediately without waiting for async
        if (durationSeconds > 0) {
          try {
            // Using navigator.sendBeacon for reliable async data sending before page unload
            const timeEntryData = {
              taskId: parseInt(currentTaskId),
              userEmail: getCookie('email'),
              durationSeconds: durationSeconds,
              description: `Time spent reviewing task ${currentTaskId} (page unload)`,
              entryDate: new Date().toISOString().split('T')[0]
            };
            
            // Create a serialized payload for sendBeacon
            const formData = new FormData();
            formData.append('data', JSON.stringify(timeEntryData));
            
            // Try to use navigator.sendBeacon if available (works better during page unload)
            if (navigator.sendBeacon) {
              const endpoint = `/api/time/log`;
              navigator.sendBeacon(endpoint, formData);
              console.log("Time log beacon sent for task:", currentTaskId);
            }
          } catch (error) {
            console.error("Failed to log time during page unload:", error);
          }
        }
      }
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    
    // Cleanup function - log time when component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      
      // Log time when leaving the page (component unmounting)
      if (taskStartTime && currentTaskId) {
        console.log(`Logging time for task ID: ${currentTaskId} on component unmount`);
        endTaskTimer();
      }
    };
  }, []);

  // Separate useEffect to load URL after data is fetched
  useEffect(() => {
    if (tasksData.length > 0 ) {
      console.log("tasksData changed, has items. Preparing to load first URL...");
      
      // Get the range from subtask summary to display correct initial row number
      const range = parseRangeFromSummary(subtaskSummary);
      const rangeStart = range ? range.start : 1;
      
      // Set initial row number based on range
      const initialRowNumber = tasksData[0]?.rowNumber || rangeStart;
      setRowNumber(initialRowNumber.toString());
      
      // Automatically load the URL when data is available
      setTimeout(() => {
        // Get the initial row number for consistent display
        const initialRowNumber = tasksData[0]?.rowNumber || rangeStart;
        setLoadingStatus(`Loading question ${initialRowNumber}...`);
        
        // Start timing for the first task
        if (tasksData[0]?.id) {
          startTaskTimer(tasksData[0].id);
        }
        
        loadUrl();
      }, 1000); // Small delay to ensure DOM is ready
    } else {
      console.log("tasksData changed but is empty:", tasksData);
    }
  }, [tasksData]);

  const getBugSummaryApi = async () => {
    try {
      const resp = await getBugSummary();
      dispatch(setBugSummary(resp));
    } catch (error) {
      console.error("Error fetching bugs summary:", error.message);
    }
  };

  const handleCreateBug = () => {
    console.log('Creating bug for projectId:', projectIdParam);
    console.log('Project details:', { clientName, projectName, guid, subtaskId });
    
    
    dispatch(setIsBugSummaryModal(true));
    getBugSummaryApi();
  };

  const handleShowBugs = async () => {
    try {
      // Get current user's first name and email for filtering
      const { getCookie } = require('../../services/utils');
      const loggedInUserFirstName = getCookie('firstName')?.toLowerCase();
      const email = getCookie('email');
      const isApprovedUser = require('../BugList/userLoginIds.json').login.includes(email);

      // Get the correct subtaskId either from URL params or from state
      const currentSubtaskId = subtaskId || location.state?.subtaskId;
      
      console.log('Fetching bugs for subtask:', currentSubtaskId);

      if (!currentSubtaskId) {
        console.error('No subtask ID available to fetch bugs');
        dispatch({ type: 'bugDetails/setSelectedProjectBugs', payload: [] });
        setShowBugsList(true);
        return;
      }

      // Fetch bugs specifically for this subtask
      const subtaskBugs = await getBugsBySubtask(currentSubtaskId);

      let filteredBugs = subtaskBugs || [];

      // Filter bugs based on user permissions if not an approved user
      // if (!isApprovedUser && loggedInUserFirstName) {
      //   filteredBugs = filteredBugs.filter(bug =>
      //     bug.assigneeDisplayName &&
      //     bug.assigneeDisplayName.toLowerCase().startsWith(loggedInUserFirstName)
      //   );
      // }

      console.log('Found', filteredBugs.length, 'bugs for subtask:', currentSubtaskId);
      dispatch({ type: 'bugDetails/setSelectedProjectBugs', payload: filteredBugs });
      setShowBugsList(true);

    } catch (error) {
      console.error('Error fetching bugs for subtask:', error);
      // Show empty bugs list on error
      dispatch({ type: 'bugDetails/setSelectedProjectBugs', payload: [] });
      setShowBugsList(true);
    }
  };

  const handleClose = async () => {
    // Log time for the current task before closing
    if (currentTaskId && taskStartTime) {
      console.log(`Logging time for current task ID: ${currentTaskId} before closing`);
      try {
        // Wait for the time logging to complete before closing
        await endTaskTimer();
        console.log("Time logged successfully before closing");
      } catch (error) {
        console.error("Error logging time before closing:", error);
      }
    }
    
    // Close the current browser tab
    console.log("Closing browser tab");
    try {
      // First attempt standard close
      window.close();
      
      // If we're still here after a moment, the close didn't work
      setTimeout(() => {
        if (window.confirm("This page is trying to close itself. Do you want to close this tab?")) {
          // Try multiple approaches to close the tab
          window.close(); // Standard approach
          window.open('', '_self').close(); // Alternative approach
          window.location.href = "about:blank"; // Last resort
        }
      }, 300);
    } catch (e) {
      console.error("Error closing tab:", e);
      alert("Please close this tab manually.");
    }
  };

  const handleCloseBugSummary = () => {
    dispatch(setIsBugSummaryModal(false));
  };

  const handleCloseBugsList = () => {
    setShowBugsList(false);
  };

  const handleNext = async () => {
    const dataArray = tasksData.length > 0 ? tasksData : [];
    const nextIndex = Math.min(currentIndex + 1, dataArray.length - 1);
    
    // If we're already at the last index, don't do anything
    if (nextIndex === currentIndex) {
      console.log('Already at the last question');
      return;
    }
    
    // End timing for current task and wait for completion before proceeding
    if (currentTaskId && taskStartTime) {
      console.log(`Ending timer for current task ID: ${currentTaskId} before moving to next`);
      await endTaskTimer();
    }
    
    // Get the range from subtask summary to display correct row numbers
    const range = parseRangeFromSummary(subtaskSummary);
    const rangeStart = range ? range.start : 1;
    
    setCurrentIndex(nextIndex);
    
    // Use the row number from the actual task data if available, otherwise calculate from range
    const actualRowNumber = dataArray[nextIndex]?.rowNumber || (rangeStart + nextIndex);
    setRowNumber(actualRowNumber.toString());
    console.log('Navigate to row:', actualRowNumber);
    
    // Start timing for the next task
    if (dataArray[nextIndex]?.id) {
      console.log(`Starting timer for next task ID: ${dataArray[nextIndex].id}`);
      startTaskTimer(dataArray[nextIndex].id);
    }
    
    // Reload iframe with new data - pass the nextIndex directly
    setTimeout(() => {
      // Pass the actual row number for consistency
      loadUrl(nextIndex);
    }, 100);
  };

  const handlePrevious = async () => {
    const dataArray = tasksData.length > 0 ? tasksData : [];
    const prevIndex = Math.max(currentIndex - 1, 0);
    
    // If we're already at the first index, don't do anything
    if (prevIndex === currentIndex) {
      console.log('Already at the first question');
      return;
    }
    
    // End timing for current task and wait for completion before proceeding
    if (currentTaskId && taskStartTime) {
      console.log(`Ending timer for current task ID: ${currentTaskId} before moving to previous`);
      await endTaskTimer();
    }
    
    // Get the range from subtask summary to display correct row numbers
    const range = parseRangeFromSummary(subtaskSummary);
    const rangeStart = range ? range.start : 1;
    
    console.log('handlePrevious - Current Index:', currentIndex);
    console.log('handlePrevious - Previous Index:', prevIndex);
    console.log('handlePrevious - Data Array Length:', dataArray.length);
    console.log('handlePrevious - Current Data:', dataArray[currentIndex]);
    console.log('handlePrevious - Previous Data:', dataArray[prevIndex]);
    
    setCurrentIndex(prevIndex);
    
    // Use the row number from the actual task data if available, otherwise calculate from range
    const actualRowNumber = dataArray[prevIndex]?.rowNumber || (rangeStart + prevIndex);
    setRowNumber(actualRowNumber.toString());
    console.log('Navigate to row:', actualRowNumber);
    
    // Start timing for the previous task
    if (dataArray[prevIndex]?.id) {
      console.log(`Starting timer for previous task ID: ${dataArray[prevIndex].id}`);
      startTaskTimer(dataArray[prevIndex].id);
    }
    
    // Reload iframe with new data - pass the prevIndex directly
    setTimeout(() => {
      loadUrl(prevIndex);
    }, 100);
  };

  const handleRowNumberChange = (e) => {
    const value = e.target.value;
    setRowNumber(value);
    // Only update the input value, don't navigate until Enter is pressed
  };

  const handleRowNumberEnter = async (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value;
      const inputRowNum = parseInt(value);
      const dataArray = tasksData.length > 0 ? tasksData : [];
      
      // Get the range from subtask summary
      const range = parseRangeFromSummary(subtaskSummary);
      const rangeStart = range ? range.start : 1;
      const rangeEnd = range ? range.end : (rangeStart + dataArray.length - 1);
      
      // Check if the entered row number is within the available range
      if (inputRowNum >= rangeStart && inputRowNum <= rangeEnd) {
        // Convert from absolute row number to array index (0-based)
        const newIndex = inputRowNum - rangeStart;
        
        if (newIndex >= 0 && newIndex < dataArray.length) {
          console.log('handleRowNumberEnter - Input Row Number:', inputRowNum);
          console.log('handleRowNumberEnter - Range:', rangeStart, '-', rangeEnd);
          console.log('handleRowNumberEnter - New Index:', newIndex);
          console.log('handleRowNumberEnter - Data Array Length:', dataArray.length);
          console.log('handleRowNumberEnter - Target Data:', dataArray[newIndex]);
          
          // If we're not moving to a different index, don't do anything
          if (newIndex === currentIndex) {
            console.log('Same index, no navigation needed');
            return;
          }
          
          // End timing for current task first, wait for completion
          if (currentTaskId && taskStartTime) {
            console.log(`Ending timer for current task ID: ${currentTaskId} before navigation`);
            await endTaskTimer();
          }
          
          setCurrentIndex(newIndex);
          
          // Start timing for the new task after the current task timer has ended
          if (dataArray[newIndex]?.id) {
            console.log(`Starting timer for new task ID: ${dataArray[newIndex].id}`);
            startTaskTimer(dataArray[newIndex].id);
          }
          
          // Reload iframe with new data - pass the newIndex directly
          setTimeout(() => {
            loadUrl(newIndex);
          }, 100);
        } else {
          // Invalid index (shouldn't happen if range is correct)
          console.warn('Invalid index calculation. Row:', inputRowNum, 'Range:', rangeStart, '-', rangeEnd);
          
          // Use actual row number from current data or calculate it
          const actualRowNumber = dataArray[currentIndex]?.rowNumber || (rangeStart + currentIndex);
          setRowNumber(actualRowNumber.toString());
        }
      } else {
        // Invalid row number, reset to current
        console.warn('Invalid row number. Must be between', rangeStart, 'and', rangeEnd);
        
        // Use actual row number from current data or calculate it
        const actualRowNumber = dataArray[currentIndex]?.rowNumber || (rangeStart + currentIndex);
        setRowNumber(actualRowNumber.toString());
      }
    }
  };  return (
    <div className="iframe-page">
      {/* Left Panel */}
      <div className={`left-panel ${isLeftPanelOpen ? 'open' : ''}`}>
        <div className="panel-header">
          <h3>Actions</h3>
          <button className="close-panel-btn" onClick={toggleLeftPanel}>
            <img src={closeIcon} alt="Close" />
          </button>
        </div>
        <div className="panel-content">
          <button className="buttonClass add-bug-btn" onClick={handleCreateBug}>
            <img src={require('../../assets/icons/addIcon.svg').default} alt="Add Bug" style={{ filter: 'invert(1)', width: '20px', height: '20px', marginRight: '3px' }} />
            <span style={{ color: '#fff', fontWeight: 500 }}>Add Bug</span>
          </button>
          <button className="buttonClass" onClick={handleShowBugs}>
            <img src={BugIcon} alt="Show Bugs" />
            Show Bugs
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Controls */}
        <div className="top-controls">
          {/* Hamburger Button */}
          <button className="hamburger-btn-fixed" onClick={toggleLeftPanel}>
            <img src={hamburgerIcon} alt="Menu" />
          </button>
          
          {/* Row Number Input and Next Button */}
          <div className="row-controls">
            <button 
              className="prev-btn" 
              onClick={handlePrevious}
              disabled={noTasksFound || dataArray.length === 0 || currentIndex <= 0}
            >
              Previous
            </button>
            <input
              type="number"
              placeholder="Row #"
              value={rowNumber}
              onChange={handleRowNumberChange}
              onKeyDown={handleRowNumberEnter}
              className="row-input"
              min="1"
              max={dataArray.length || 1}
              disabled={noTasksFound || dataArray.length === 0}
            />
            <button 
              className="next-btn" 
              onClick={handleNext}
              disabled={noTasksFound || dataArray.length === 0 || currentIndex >= dataArray.length - 1}
            >
              Next
            </button>
          </div>

          {/* Close Button */}
          <button className="back-btn-fixed" onClick={handleClose}>
            Close
          </button>
        </div>
        
        {/* Slide Number and LO ID Display */}
        {currentData && (
          <div className="slide-info-container">
            <div className="slide-info-box">
              <span className="slide-info-label">Slide Number:</span>
              <input 
                className="slide-info-value copyable" 
                value={currentData.slideNumber || "N/A"} 
                readOnly 
                onClick={(e) => e.target.select()} 
                title="Click to select and copy" 
              />
            </div>
            <div className="slide-info-box">
              <span className="slide-info-label">LO ID:</span>
              <input 
                className="slide-info-value copyable" 
                value={currentData.loId || "N/A"} 
                readOnly 
                onClick={(e) => e.target.select()} 
                title="Click to select and copy" 
              />
            </div>
          </div>
        )}

        {/* iframe Container */}
        <div className="iframe-container">
          {noTasksFound ? (
            <div className="loading" style={{ 
              color: '#f44336', 
              fontSize: '18px', 
              textAlign: 'center', 
              padding: '40px 20px',
              backgroundColor: '#fff3e0',
              border: '2px solid #ff9800',
              borderRadius: '8px',
              margin: '20px'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#e65100' }}>
                No Questions Found
              </h3>
              <p style={{ margin: '0 0 20px 0', color: '#bf360c' }}>
                No questions found for this subtask range.
              </p>
              <button 
                className="buttonClass" 
                onClick={handleClose}
                style={{
                  backgroundColor: '#ff9800',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Show loading message or error message using React state */}
              {iframeLoading && (
                <div className="loading">
                  {!loadingError ? (
                    <>
                      <div style={{ 
                        fontSize: '20px', 
                        marginBottom: '15px', 
                        fontWeight: 'bold',
                        color: '#007bba' 
                      }}>
                        Loading Question {rowNumber}
                      </div>
                      {/* <div style={{ fontSize: '14px', marginBottom: '20px', color: '#666' }}>
                        {loadingStatus}
                      </div> */}
                      <div className="loading-spinner"></div>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        fontSize: '20px', 
                        marginBottom: '15px', 
                        fontWeight: 'bold',
                        color: '#d32f2f' 
                      }}>
                        Error Loading Content
                      </div>
                      <div style={{ fontSize: '14px', marginBottom: '20px', color: '#666' }}>
                        {loadingError}
                      </div>
                      <button 
                        onClick={() => window.location.reload()} 
                        style={{ padding: '8px 16px', margin: '10px', cursor: 'pointer' }}
                      >
                        Refresh Page
                      </button>
                    </>
                  )}
                </div>
              )}
              <iframe 
                ref={iframeRef}
                style={{ display: iframeLoading ? 'none' : 'block' }} 
                title="Content Iframe"
                sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts"
              ></iframe>
            </>
          )}
        </div>
      </div>

      {/* Overlay for mobile */}
      {isLeftPanelOpen && <div className="overlay" onClick={toggleLeftPanel}></div>}

      {/* Bug Summary Modal */}
          {isBugSummaryModal && (
        <div className="modal-overlay" onClick={handleCloseBugSummary}>
          <div className="modal-container bug-summary-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Bug</h2>
              <button className="close-modal-btn" onClick={handleCloseBugSummary}>
                <img src={closeIcon} alt="Close" />
              </button>
            </div>
            <BugSummary 
              popupOpen={isBugSummaryModal} 
              handleClose={handleCloseBugSummary}
              projectId={projectIdParam}
              projectName={projectName}
              clientName={clientName}
              subtaskId={subtaskId}
              subtaskName={subtaskName} 
              subtaskSummary={subtaskSummary}
              fromIframe={true}
              // Pass current slide info for additional context
              slideNumber={currentData?.slideNumber}
              loId={currentData?.loId}
            />
          </div>
        </div>
      )}      {/* Bugs List Modal */}
      {showBugsList && (
        <div className="modal-overlay" onClick={handleCloseBugsList}>
          <div className="modal-container bugs-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bugs List</h2>
              <button className="close-modal-btn" onClick={handleCloseBugsList}>
                <img src={closeIcon} alt="Close" />
              </button>
            </div>
            <div className="bug-table">
              <table>
                <thead>
                  <tr>
                    <th className="thead-th">S. No.</th>
                    <th className="thead-th" style={{ textAlign: "left" }}>Bug Title</th>
                    <th className="thead-th">Created By</th>
                    <th className="thead-th">Severity</th>
                    <th className="thead-th">WCAG</th>
                    <th className="thead-th">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(selectedProjectBugs) ? selectedProjectBugs : []).map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? "evenRow" : "oddRow"}>
                      <td>{index + 1}</td>
                      <td style={{ textAlign: "left" }}>{item?.bugSummary?.name || "N/A"}</td>
                      <td>{item?.createdBy || "N/A"}</td>
                      <td>{item?.bugSummary?.severity || "N/A"}</td>
                      <td>{item?.bugSummary?.wcagLevel || "N/A"}</td>
                      <td style={{ fontWeight: 700 }}>{item?.status?.value || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IframePage;
